// DNAChat · Painel de Templates WhatsApp (V4.62)
// - Lista templates cacheados (db.whatsapp_templates)
// - Sincroniza com Meta Graph API (aprovados/rejeitados)
// - Deleta templates locais
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { FileText, RefreshCw, Loader2, ChevronLeft, Trash2, CheckCircle2, XCircle, Clock } from 'lucide-react';

const STATUS_STYLE = {
  APPROVED: ['bg-emerald-50 border-emerald-200 text-emerald-700', CheckCircle2],
  PENDING:  ['bg-amber-50 border-amber-200 text-amber-700', Clock],
  REJECTED: ['bg-rose-50 border-rose-200 text-rose-700', XCircle],
  PAUSED:   ['bg-orange-50 border-orange-200 text-orange-700', Clock],
  DISABLED: ['bg-slate-100 border-slate-200 text-slate-500', XCircle],
};

const fmtDT = (iso) => { try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso || '—'; } };

export default function WhatsAppTemplatesPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [lastSyncSummary, setLastSyncSummary] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/dnachat/whatsapp/templates', { params: { _bust: Date.now() } });
      setItems(data?.items || []);
      setLastSyncedAt(data?.last_synced_at || null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao carregar templates');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/dnachat/whatsapp/templates/sync');
      const created = data?.created ?? 0;
      const updated = data?.updated ?? 0;
      const unchanged = data?.unchanged ?? 0;
      const fetched = data?.fetched ?? 0;
      const missing = data?.remote_missing_marked ?? 0;
      setLastSyncSummary({ ...data });
      toast.success(`${fetched} template(s) na Meta · ${created} novo(s) · ${updated} atualizado(s)${unchanged ? ` · ${unchanged} inalterado(s)` : ''}${missing ? ` · ${missing} sumiu na Meta` : ''}`);
      await fetchData();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === 'object' && d) ? (d.message || d.provider_error?.error?.message || JSON.stringify(d).slice(0, 200)) : (typeof d === 'string' ? d : 'Falha ao sincronizar');
      toast.error(msg);
    } finally { setSyncing(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir template "${t.name}" (${t.language}) do cache local?`)) return;
    setDeleting(`${t.name}:${t.language}`);
    try {
      await api.delete(`/dnachat/whatsapp/templates/${encodeURIComponent(t.name)}?language=${encodeURIComponent(t.language || 'pt_BR')}`);
      toast.success('Template removido do cache local');
      fetchData();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao remover');
    } finally { setDeleting(null); }
  };

  const statusPill = (s) => {
    const [cls, Icon] = STATUS_STYLE[s] || ['bg-slate-100 border-slate-200 text-slate-600', FileText];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase font-bold ${cls}`}>
        <Icon className="w-3 h-3" /> {s || '—'}
      </span>
    );
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="wa-templates">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <button type="button" onClick={() => navigate('/v2/chat/whatsapp/config')}
            className="text-xs text-slate-500 hover:text-slate-800 mb-1 inline-flex items-center gap-1"
            data-testid="wa-templates-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Voltar para Config
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-8 h-8 text-slate-700" /> Templates do WhatsApp
          </h1>
          <p className="text-sm text-slate-500 mt-1">Cache local dos templates sincronizados da Meta (todos os status/idiomas/categorias). Use &quot;Sincronizar&quot; para puxar os mais recentes.</p>
          {lastSyncedAt && (
            <p className="text-[11px] text-slate-500 mt-1" data-testid="wa-templates-last-sync">
              Última sincronização com Meta: <b>{fmtDT(lastSyncedAt)}</b>
              {lastSyncSummary?.waba_id ? <span className="ml-2 font-mono text-[10px] text-slate-400">WABA · {lastSyncSummary.waba_id}</span> : null}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={sync} disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="wa-templates-sync">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sincronizar com Meta
          </button>
          <button type="button" onClick={fetchData} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="wa-templates-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Atualizar
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="wa-templates-table">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Nome</th>
                <th className="text-left px-3 py-2">Idioma</th>
                <th className="text-left px-3 py-2">Categoria</th>
                <th className="text-center px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Sincronizado</th>
                <th className="text-right px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400 text-sm">
                  Nenhum template no cache. Clique em &quot;Sincronizar com Meta&quot; para importar.
                </td></tr>
              )}
              {items.map((t) => {
                const key = `${t.name}:${t.language}`;
                const missing = !!t.remote_missing;
                return (
                  <tr key={key} className={`border-t border-slate-100 ${missing ? 'bg-rose-50/40' : ''}`} data-testid={`wa-tpl-row-${t.name}`}>
                    <td className="px-3 py-2 font-mono text-[12px] text-slate-800">
                      {t.name}
                      {missing && <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[9px] font-bold uppercase">sumiu na Meta</span>}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-600">{t.language || 'pt_BR'}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-600">{t.category || '—'}</td>
                    <td className="px-3 py-2 text-center">{statusPill(t.status)}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{fmtDT(t.last_synced_at || t.synced_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" onClick={() => remove(t)} disabled={deleting === key}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-xs font-semibold disabled:opacity-50"
                        data-testid={`wa-tpl-delete-${t.name}`}>
                        {deleting === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Remover
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-3">
        A remoção afeta apenas o cache local; para excluir na Meta, use o Meta Business Manager.
      </p>
    </div>
  );
}
